import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { 
  Filter, 
  ShieldCheck, 
  Package, 
  FlaskRound, 
  Microscope, 
  Truck, 
  Sprout, 
  BarChart 
} from "lucide-react";

const Service = () => {
  const services = [
    {
      icon: <Filter className="h-8 w-8 text-primary" />,
      title: "Seed Cleaning & Grading",
      description: "Our state-of-the-art facilities ensure precise cleaning and grading of all seed types, removing impurities and sorting seeds by size, density, and quality for optimal performance.",
      image: "https://images.unsplash.com/photo-1585598007006-47f1f80afb84?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxfDB8MXxyYW5kb218MHx8c2VlZCBwcm9jZXNzaW5nIGVxdWlwbWVudHx8fHx8fDE2OTc4Mzg5Njg&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=500"
    },
    {
      icon: <ShieldCheck className="h-8 w-8 text-primary" />,
      title: "Seed Treatment",
      description: "We offer comprehensive seed treatment services that protect seeds from diseases, pests, and environmental stresses, enhancing germination rates and early seedling vigor.",
      image: "https://images.unsplash.com/photo-1577639576759-a38a1ed09fe7?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxfDB8MXxyYW5kb218MHx8c2VlZCBwcm9jZXNzaW5nIGVxdWlwbWVudHx8fHx8fDE2OTc4Mzg5ODE&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=500"
    },
    {
      icon: <Package className="h-8 w-8 text-primary" />,
      title: "Seed Packaging",
      description: "Our custom packaging solutions ensure seed viability and longevity through moisture-resistant, tamper-evident packaging that maintains seed quality throughout the supply chain.",
      image: "https://images.unsplash.com/photo-1598717873646-ba7e5ab01a39?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxfDB8MXxyYW5kb218MHx8c2VlZCBwcm9jZXNzaW5nIGVxdWlwbWVudHx8fHx8fDE2OTc4Mzg5OTY&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=500"
    },
    {
      icon: <FlaskRound className="h-8 w-8 text-primary" />,
      title: "Seed Testing",
      description: "Our comprehensive testing services assess germination rate, purity, vigor, and genetic integrity, ensuring that every batch of seeds meets international quality standards.",
      image: "https://images.unsplash.com/photo-1605000797499-95a51c5269ae?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxfDB8MXxyYW5kb218MHx8c2VlZCBwcm9jZXNzaW5nIGVxdWlwbWVudHx8fHx8fDE2OTc4MzkwMDk&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=500"
    }
  ];
  
  const additionalServices = [
    {
      icon: <Microscope className="h-8 w-8 text-primary" />,
      title: "Research & Development",
      description: "Our R&D team works continuously to develop new seed varieties with improved traits for yield, disease resistance, and climate adaptation."
    },
    {
      icon: <Truck className="h-8 w-8 text-primary" />,
      title: "Logistics & Distribution",
      description: "We provide reliable seed distribution services to ensure timely delivery of high-quality seeds to farmers across the country."
    },
    {
      icon: <Sprout className="h-8 w-8 text-primary" />,
      title: "Farming Consultation",
      description: "Our agricultural experts offer personalized consultation to help farmers implement best practices for optimal crop yield."
    },
    {
      icon: <BarChart className="h-8 w-8 text-primary" />,
      title: "Crop Performance Analysis",
      description: "We provide detailed analysis of crop performance to help farmers make data-driven decisions for future planting seasons."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Services - Varsha Seeds</title>
        <meta name="description" content="Explore our comprehensive seed processing services including cleaning, treatment, packaging, and testing. Varsha Seeds delivers excellence in agricultural solutions." />
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-primary relative overflow-hidden py-24">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1605000797499-95a51c5269ae?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=MnwxfDB8MXxyYW5kb218MHx8c2VlZCBwcm9jZXNzaW5nIGVxdWlwbWVudHx8fHx8fDE2OTc4MzkwMDk&ixlib=rb-4.0.3&q=80&w=1920" 
            alt="Seed processing equipment" 
            className="h-full w-full object-cover opacity-20"
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-heading mb-6">
            Our Services
          </h1>
          <p className="text-white text-lg md:text-xl max-w-3xl mx-auto">
            We provide comprehensive seed processing services using cutting-edge technology
            and industry best practices to ensure optimal seed quality and performance.
          </p>
        </div>
      </section>
      
      {/* Core Services Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-primary font-medium">Core Services</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4 font-heading">
              Advanced Seed Processing Solutions
            </h2>
            <p className="text-neutral-700">
              Our comprehensive seed processing services ensure the highest quality 
              from cleaning to packaging, delivering seeds that perform exceptionally in the field.
            </p>
          </div>
          
          <div className="space-y-16">
            {services.map((service, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}>
                <div className={index % 2 === 1 ? 'order-1 lg:order-2' : ''}>
                  <div className="flex gap-4 mb-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center">
                      {service.icon}
                    </div>
                    <h3 className="text-2xl font-bold font-heading self-center">{service.title}</h3>
                  </div>
                  
                  <p className="text-neutral-700 mb-6 text-lg">{service.description}</p>
                  
                  <ul className="space-y-3 mb-8">
                    {[1, 2, 3].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center mt-1 flex-shrink-0">
                          <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <p className="text-neutral-700">
                          {service.title === "Seed Cleaning & Grading" && [
                            "Removal of impurities, broken seeds, and foreign material",
                            "Size grading for uniform seed dimensions",
                            "Density separation for higher quality seed lots"
                          ][item - 1]}
                          
                          {service.title === "Seed Treatment" && [
                            "Protection against soil-borne diseases and pests",
                            "Application of growth enhancers and micronutrients",
                            "Polymer coating for improved handling and planting"
                          ][item - 1]}
                          
                          {service.title === "Seed Packaging" && [
                            "Moisture-resistant packaging materials",
                            "Tamper-evident seals for product security",
                            "Clear labeling with complete product information"
                          ][item - 1]}
                          
                          {service.title === "Seed Testing" && [
                            "Germination and vigor testing under controlled conditions",
                            "Purity analysis for varietal authenticity",
                            "Disease and pest screening for healthy seed lots"
                          ][item - 1]}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className={index % 2 === 1 ? 'order-2 lg:order-1' : ''}>
                  <img 
                    src={service.image} 
                    alt={service.title} 
                    className="rounded-lg shadow-lg w-full"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Additional Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-primary font-medium">Additional Services</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4 font-heading">
              Supporting Your Agricultural Success
            </h2>
            <p className="text-neutral-700">
              Beyond seed processing, we offer a range of complementary services to 
              support farmers throughout the agricultural cycle.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {additionalServices.map((service, index) => (
              <div key={index} className="bg-background p-8 rounded-lg shadow-md">
                <div className="flex gap-4 mb-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-primary bg-opacity-10 rounded-full flex items-center justify-center">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold font-heading self-center">{service.title}</h3>
                </div>
                <p className="text-neutral-700 mb-4">{service.description}</p>
                <Link href="/contact">
                  <a className="text-primary font-medium hover:text-primary-dark transition inline-flex items-center">
                    Learn more
                    <svg className="ml-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </a>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Process Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-primary font-medium">Our Process</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4 font-heading">
              How We Deliver Excellence
            </h2>
            <p className="text-neutral-700">
              Our systematic approach ensures quality at every step of the seed processing journey.
            </p>
          </div>
          
          <div className="relative">
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 bg-neutral-200 transform -translate-x-1/2"></div>
            
            <div className="space-y-12">
              {[
                {
                  step: 1,
                  title: "Initial Assessment",
                  description: "We begin by analyzing your seed lot to determine its specific needs and create a customized processing plan."
                },
                {
                  step: 2,
                  title: "Processing & Treatment",
                  description: "Your seeds undergo our comprehensive cleaning, grading, and treatment processes based on the assessment."
                },
                {
                  step: 3,
                  title: "Quality Testing",
                  description: "Rigorous testing ensures that the processed seeds meet our high standards for germination, purity, and vigor."
                },
                {
                  step: 4,
                  title: "Packaging & Delivery",
                  description: "The approved seed lots are carefully packaged and prepared for timely delivery to your location."
                }
              ].map((step, index) => (
                <div key={index} className={`flex flex-col md:flex-row gap-8 relative ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                  <div className="md:w-1/2 flex justify-center md:justify-end">
                    <div className={`bg-white p-6 rounded-lg shadow-md max-w-md ${index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}`}>
                      <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center mb-4">
                        <span className="font-bold">{step.step}</span>
                      </div>
                      <h3 className="text-xl font-semibold mb-2 font-heading">{step.title}</h3>
                      <p className="text-neutral-700">{step.description}</p>
                    </div>
                  </div>
                  
                  <div className="md:w-1/2 hidden md:block"></div>
                  
                  <div className="absolute left-1/2 top-6 w-6 h-6 bg-primary rounded-full transform -translate-x-1/2 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-accent font-medium">Success Stories</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4 text-white font-heading">
              What Clients Say About Our Services
            </h2>
            <p className="text-white text-opacity-90">
              Don't just take our word for it. Here's what clients have to say about 
              our seed processing services.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Vijay Agro Industries",
                location: "Punjab, India",
                image: "https://images.unsplash.com/photo-1560343776-97e7d202ff0e?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=56&ixid=MnwxfDB8MXxyYW5kb218MHx8ZmFybWVyfHx8fHx8MTY5NzgzOTAzMw&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=56",
                comment: "Varsha Seeds' processing services have transformed our seed quality. Their meticulous attention to detail and advanced technology have helped us deliver premium seeds to our customers."
              },
              {
                name: "Green Earth Farmers Cooperative",
                location: "Maharashtra, India",
                image: "https://images.unsplash.com/photo-1591025810539-a321000c2096?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=56&ixid=MnwxfDB8MXxyYW5kb218MHx8ZmFybWVyfHx8fHx8MTY5NzgzOTA1MA&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=56",
                comment: "We've been using Varsha Seeds' seed testing and treatment services for three years. The improvement in germination rates and seedling vigor has been remarkable, leading to significantly better crop yields."
              },
              {
                name: "Sunrise Agricultural Exports",
                location: "Gujarat, India",
                image: "https://images.unsplash.com/photo-1597320554048-60857bb30d97?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=56&ixid=MnwxfDB8MXxyYW5kb218MHx8ZmFybWVyfHx8fHx8MTY5NzgzOTA2NA&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=56",
                comment: "The custom packaging solutions provided by Varsha Seeds have greatly extended our products' shelf life and enhanced our brand image in international markets. Their attention to quality is unmatched."
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg">
                <div className="flex gap-4 items-center mb-4">
                  <div className="w-14 h-14 bg-neutral-100 rounded-full overflow-hidden">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="font-semibold font-heading">{testimonial.name}</h4>
                    <p className="text-sm text-neutral-700">{testimonial.location}</p>
                  </div>
                </div>
                <p className="text-neutral-700 mb-4">{testimonial.comment}</p>
                <div className="flex text-accent">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="h-5 w-5 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="bg-accent/10 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-primary font-heading">
              Ready to Elevate Your Seed Quality?
            </h2>
            <p className="text-neutral-700 mb-8 max-w-3xl mx-auto">
              Contact us today to learn how our seed processing services can help you deliver 
              premium quality seeds to your customers and improve crop yields.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link href="/contact">
                <Button className="bg-primary hover:bg-primary/90 text-white rounded-full">
                  Request a Service Quote
                </Button>
              </Link>
              <Link href="/products">
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white rounded-full">
                  Explore Our Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Service;
